/* eslint-disable react/no-array-index-key */
import React from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Icon,
  IconButton,
} from "@mui/material";
import { cloneDeep } from "lodash";
import MDBox from "components/MDBox";
import MultiSelectDropdown from "components/SettingFields/atoms/MultiSelectDropdown";
import SingleSelectDropdown from "components/SettingFields/atoms/SingleSelectDropdown";
import BooleanSetting from "components/SettingFields/atoms/BooleanSetting";
import CheckboxGroup from "components/SettingFields/atoms/CheckboxGroup";
import MDButton from "components/MDButton";

const AddNewInsightsButton = ({ onClick }) => (
  <MDButton onClick={onClick} color="secondaryRed" variant="text">
    + Add new insight
  </MDButton>
);

const tableCellPadding = {
  paddingLeft: "0.5rem",
  paddingRight: "0.5rem",
};

const InsightsFormTableComponent = ({
  settings,
  clonedSetting,
  setClonedSetting,
  usedKpis,
  handleCustomOptionModalOpen,
  setHasChanges,
  deleteRow,
  duplicateRow,
}) => (
  <Card sx={{ my: 3, p: 2 }}>
    <TableContainer
      component={Paper}
      sx={{
        marginBottom: "24px",
      }}
    >
      <Table
        sx={{
          minWidth: 650,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            <TableCell
              key="table-header-kpi"
              style={{
                width: "19%",
                ...tableCellPadding,
              }}
            >
              KPI
            </TableCell>
            <TableCell
              key="table-header-reportOn"
              style={{
                width: "16%",
                ...tableCellPadding,
              }}
            >
              Report On
            </TableCell>
            <TableCell
              key="table-header-insights-alert-type"
              style={{
                width: "16%",
                ...tableCellPadding,
              }}
            >
              Insights Alert Type
            </TableCell>
            <TableCell
              key="table-header-parameters"
              style={{
                width: "17%",
                ...tableCellPadding,
              }}
            >
              Threshold
            </TableCell>
            <TableCell
              key="table-header-trailing-average"
              style={{
                width: "15%",
                ...tableCellPadding,
              }}
            >
              Trailing Average
            </TableCell>
            <TableCell
              key="table-header-alert-type"
              style={{
                width: "16%",
                ...tableCellPadding,
              }}
            >
              Alert Type
            </TableCell>
            <TableCell
              key="table-header-status"
              style={{
                width: "1%",
                ...tableCellPadding,
              }}
            >
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* loop through every single row */}
          {clonedSetting?.alerts?.length
            ? clonedSetting.alerts.map((rows, parentIndex) =>
                rows.map((row, index) => {
                  // If any one column contains duplicate functionality then need to align complete row at baseline to fix the center UI
                  const shouldAlignRowAtBaseline = true;
                  const showDarkBorder = rows.length === 1 || index === rows.length - 1;

                  return (
                    <TableRow
                      key={`table-row-wrapper-${parentIndex}-${index}-${row.key}`}
                      data-testid={`table-row-wrapper-${parentIndex}-${index}-${row.key}`}
                      sx={{
                        ...(showDarkBorder
                          ? {
                              "& td": {
                                borderColor: "#a9a9a9",
                              },
                            }
                          : {}),
                      }}
                    >
                      {/* column-1 */}
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {index === 0 ? (
                        <TableCell
                          key={`table-row-${parentIndex}-${index}-kpi`}
                          data-testid={`table-row-${parentIndex}-${index}-kpi`}
                          sx={{
                            ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                            ...tableCellPadding,
                          }}
                        >
                          <MDBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                            }}
                          >
                            <SingleSelectDropdown
                              name={`table-row-${parentIndex}-${index}-kpi-field-${row.key}`}
                              setting={{
                                columnId: `table-row-${parentIndex}-${index}-kpi-field`,
                                en_field_label: "Kpi",
                                field_value: row?.kpi || null,
                                settings_data_type: "multi-select-dropdown",
                                options:
                                  settings?.dropDownOptions?.kpis?.filter(
                                    (kpi) => !usedKpis.includes(kpi.value)
                                  ) || [],
                              }}
                              passWholeOption
                              setParentState={(newChildValue) => {
                                setClonedSetting((prev) => {
                                  const clone = cloneDeep(prev);
                                  clone.alerts[parentIndex][index].kpi = newChildValue;
                                  return clone;
                                });

                                setHasChanges(true);
                              }}
                              validations={{
                                validate: (value) =>
                                  value?.value ?? value ? true : "KPI is required",
                              }}
                              multiline
                            />
                            {rows.length > 1 ? null : (
                              <AddNewInsightsButton onClick={() => duplicateRow(parentIndex)} />
                            )}
                          </MDBox>
                        </TableCell>
                      ) : index === rows.length - 1 ? (
                        <TableCell
                          key={`table-row-${parentIndex}-${index}-duplicate`}
                          sx={{ verticalAlign: "bottom", ...tableCellPadding }}
                        >
                          <AddNewInsightsButton onClick={() => duplicateRow(parentIndex)} />
                        </TableCell>
                      ) : (
                        <TableCell
                          key={`table-row-${parentIndex}-${index}-empty`}
                          sx={{
                            ...tableCellPadding,
                          }}
                        />
                      )}

                      {/* column-2 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-teamIds`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                          ...tableCellPadding,
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <MultiSelectDropdown
                            name={`table-row-${parentIndex}-${index}-team-ids-field-${row.key}`}
                            setting={{
                              columnId: `table-row-${parentIndex}-${index}-team-ids-field`,
                              en_field_label: "Report On",
                              field_value: row?.teamIds || [],
                              settings_data_type: "multi-select-dropdown",
                              options: settings?.dropDownOptions?.teamIds || [],
                            }}
                            disableCloseOnSelect
                            setParentState={(newChildValue) => {
                              setClonedSetting((prev) => {
                                const clone = cloneDeep(prev);
                                clone.alerts[parentIndex][index].teamIds = newChildValue;
                                return clone;
                              });
                              setHasChanges(true);
                            }}
                            validations={{
                              validate: (value) =>
                                value?.length < 1 ? "Please select at least one team" : true,
                            }}
                          />
                        </MDBox>
                      </TableCell>
                      {/* column-3 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-reportOn`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                          ...tableCellPadding,
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <CheckboxGroup
                            checkboxes={settings?.dropDownOptions?.reportOn || []}
                            checkboxFilters={row?.reportOn}
                            setCheckboxFilters={(newChildValue) => {
                              setClonedSetting((prev) => {
                                const clone = cloneDeep(prev);
                                clone.alerts[parentIndex][index].reportOn = newChildValue;
                                return clone;
                              });
                              setHasChanges(true);
                            }}
                          />
                        </MDBox>
                      </TableCell>
                      {/* column-4 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-parameters`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                          ...tableCellPadding,
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SingleSelectDropdown
                            name={`table-row-${parentIndex}-${index}-parameters-field-${row.key}`}
                            setting={{
                              columnId: `table-row-${parentIndex}-${index}-parameters-field`,
                              en_field_label: "Threshold",
                              field_value: row?.parameters || null,
                              settings_data_type: "multi-select-dropdown",
                              options: settings?.dropDownOptions?.parameters || [],
                            }}
                            passWholeOption
                            setParentState={(newChildValue) => {
                              if (newChildValue?.value === "custom") {
                                handleCustomOptionModalOpen({
                                  for: "parameters",
                                  pathToUpdate: ["alerts", parentIndex, index, "parameters"],
                                  optionToUpdate: "parameters",
                                });
                              } else {
                                setClonedSetting((prev) => {
                                  const clone = cloneDeep(prev);
                                  clone.alerts[parentIndex][index].parameters = newChildValue;
                                  return clone;
                                });
                                setHasChanges(true);
                              }
                            }}
                            validations={{
                              validate: (value) =>
                                value?.value ?? value ? true : "Parameter is required",
                            }}
                            multiline
                          />
                        </MDBox>
                      </TableCell>
                      {/* column-5 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-period`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                          ...tableCellPadding,
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SingleSelectDropdown
                            name={`table-row-${parentIndex}-${index}-period-field-${row.key}`}
                            setting={{
                              columnId: `table-row-${parentIndex}-${index}-period-field`,
                              en_field_label: "Trailing Average",
                              field_value: row?.period || null,
                              settings_data_type: "multi-select-dropdown",
                              options: settings?.dropDownOptions?.period || [],
                            }}
                            passWholeOption
                            setParentState={(newChildValue) => {
                              if (newChildValue?.value === "custom") {
                                handleCustomOptionModalOpen({
                                  for: "trailingAverage",

                                  pathToUpdate: ["alerts", parentIndex, index, "period"],
                                  optionToUpdate: "period",
                                });
                              } else {
                                setClonedSetting((prev) => {
                                  const clone = cloneDeep(prev);
                                  clone.alerts[parentIndex][index].period = newChildValue;
                                  return clone;
                                });
                                setHasChanges(true);
                              }
                            }}
                            validations={{
                              validate: (value) =>
                                value?.value ?? value ? true : "Trailing Average is required",
                            }}
                          />
                        </MDBox>
                      </TableCell>
                      {/* column-6 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-interval`}
                        sx={{
                          ...(shouldAlignRowAtBaseline ? { verticalAlign: "baseline" } : {}),
                          ...tableCellPadding,
                        }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SingleSelectDropdown
                            name={`table-row-${parentIndex}-${index}-interval-field-${row.key}`}
                            setting={{
                              columnId: `table-row-${parentIndex}-${index}-interval-field`,
                              en_field_label: "Alert Type",
                              field_value: row?.interval || null,
                              settings_data_type: "multi-select-dropdown",
                              options: settings?.dropDownOptions?.interval || [],
                            }}
                            passWholeOption
                            setParentState={(newChildValue) => {
                              setClonedSetting((prev) => {
                                const clone = cloneDeep(prev);
                                clone.alerts[parentIndex][index].interval = newChildValue;
                                return clone;
                              });
                              setHasChanges(true);
                            }}
                            validations={{
                              validate: (value) =>
                                value?.value ?? value ? true : "Alert Type is required",
                            }}
                          />
                        </MDBox>
                      </TableCell>
                      {/* column-7 */}
                      <TableCell
                        key={`table-row-${parentIndex}-${index}-status`}
                        sx={{ verticalAlign: "middle", ...tableCellPadding }}
                      >
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <BooleanSetting
                            setting={{
                              en_field_label: "",
                              field_value: row?.status ?? true,
                            }}
                            setParentState={(newChildValue) => {
                              setClonedSetting((prev) => {
                                const clone = cloneDeep(prev);
                                clone.alerts[parentIndex][index].status = !!newChildValue;
                                return clone;
                              });
                              setHasChanges(true);
                            }}
                          />
                          <IconButton
                            size="small"
                            color="inherit"
                            variant="contained"
                            onClick={() => {
                              deleteRow(parentIndex, index);
                              setHasChanges(true);
                            }}
                          >
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                        </MDBox>
                      </TableCell>
                    </TableRow>
                  );
                })
              )
            : null}
          <TableRow key="table-row-wrapper-add-kpi">
            <TableCell
              key="table-row-add-kpi-cell"
              sx={{
                ...tableCellPadding,
              }}
            >
              <MDButton onClick={() => duplicateRow()}>+ Add KPI</MDButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

export default InsightsFormTableComponent;
